import { useProfile } from '@shared/hooks/useProfile';
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../../config/firebase';
import { Message, MessageUpdates } from '../types';

const TYPING_TIMEOUT = 2000;

export const useMessageUpdates = (
  conversationId: string,
  messages: Message[],
  setMessages: (messages: Message[]) => void
) => {
  const { user } = useProfile();
  const [isTyping, setIsTyping] = useState<{ [key: string]: boolean }>({});
  const [typingTimeout, setTypingTimeout] =
    useState<ReturnType<typeof setTimeout>>();

  // Subscribe to message updates
  useEffect(() => {
    if (!conversationId || !user?.id) return;

    const updatesRef = doc(db, 'messageUpdates', conversationId);

    // Create updates document if it doesn't exist
    const initializeUpdates = async () => {
      const doc = await getDoc(updatesRef);
      if (!doc.exists()) {
        await setDoc(updatesRef, {
          conversationId,
          senderUpdates: [],
          receiverUpdates: [],
          typingUsers: {},
        });
      }
    };
    initializeUpdates();

    // Listen for updates
    const unsubscribe = onSnapshot(updatesRef, async (snapshot) => {
      if (!snapshot.exists()) return;

      const updates = snapshot.data() as MessageUpdates;

      // Handle typing indicators
      const typingStates: { [key: string]: boolean } = {};
      Object.entries(updates.typingUsers || {}).forEach(([userId, data]) => {
        if (userId !== user.id) {
          const lastTyping = data.lastTypingTimestamp?.toDate();
          typingStates[userId] =
            data.isTyping &&
            lastTyping &&
            Date.now() - lastTyping.getTime() < TYPING_TIMEOUT;
        }
      });
      setIsTyping(typingStates);

      // Handle message updates for current user
      const userUpdates = (updates.senderUpdates || []).filter((update) =>
        messages.find(
          (msg) => msg.senderId === user.id && msg.id === update.messageId
        )
      );
      const otherUpdates = (updates.receiverUpdates || []).filter((update) =>
        messages.find(
          (msg) => msg.senderId !== user.id && msg.id === update.messageId
        )
      );

      const allUpdates = [...userUpdates, ...otherUpdates];
      if (allUpdates.length > 0) {
        // Update local messages
        const updatedMessages = messages.map((msg) => {
          const update = allUpdates.find((u) => u.messageId === msg.id);
          if (update?.type === 'delete') {
            return {
              ...msg,
              type: 'deleted' as const,
              content: '',
              mediaUrl: undefined,
            };
          }
          return msg;
        });

        setMessages(updatedMessages);

        // Clear processed updates
        await updateDoc(updatesRef, {
          senderUpdates: (updates.senderUpdates || []).filter(
            (update) =>
              !userUpdates.some((u) => u.messageId === update.messageId)
          ),
          receiverUpdates: (updates.receiverUpdates || []).filter(
            (update) =>
              !otherUpdates.some((u) => u.messageId === update.messageId)
          ),
        });
      }
    });

    return () => unsubscribe();
  }, [conversationId, user?.id, messages]);

  const updateTypingStatus = async (isTyping: boolean) => {
    if (!conversationId || !user?.id) return;

    const updatesRef = doc(db, 'messageUpdates', conversationId);

    await updateDoc(updatesRef, {
      [`typingUsers.${user.id}`]: {
        isTyping,
        lastTypingTimestamp: serverTimestamp(),
      },
    });

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    if (isTyping) {
      const timeout = setTimeout(async () => {
        await updateTypingStatus(false);
      }, TYPING_TIMEOUT);
      setTypingTimeout(timeout);
    }
  };

  const addMessageUpdate = async (
    messageId: string,
    type: 'delete' | 'edit'
  ) => {
    console.log(messageId, type);

    if (!conversationId || !user?.id) return;

    const updatesRef = doc(db, 'messageUpdates', conversationId);
    const update = {
      messageId,
      type,
      timestamp: new Date(),
    };

    console.log(update);

    const updatesDoc = await getDoc(updatesRef);
    if (!updatesDoc.exists()) {
      await setDoc(updatesRef, {
        conversationId,
        senderUpdates: [update],
        receiverUpdates: [update],
        typingUsers: {},
      });
    } else {
      const data = updatesDoc.data() as MessageUpdates;
      await updateDoc(updatesRef, {
        senderUpdates: [...(data.senderUpdates || []), update],
        receiverUpdates: [...(data.receiverUpdates || []), update],
      });
    }
  };

  return {
    isTyping,
    updateTypingStatus,
    addMessageUpdate,
  };
};
